import { Component, inject, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { AccordionComponent } from '@ppl/ui';
import { switchLeadContext } from '../../../shared/utils/lead-context.util';
import { AuthorizeDirective } from '../../../shared/authorization/authorize.directive.';
import { AuthorizationUtility } from '../../../shared/authorization/auth.utility';
import { MEMBER_VIEW_PAGE_IDS, PAGE_CONSTANTS, TABS } from '../../../shared/constants/page.constants';
import { SectionsService } from '../../../services/sections.service';
import { Router } from '@angular/router';
import { STATIC_DATA, STATIC_JSON_DATA_PATH } from '../../../shared/constants/static-data.constants';
import {  ListTableComponent  } from '@ppl/ui';
import { TableRowActionsModel } from '../../../shared/interfaces/list.interface';
import { TableRowActions } from '../../../shared/interfaces/actions';
import { NgZorroModule } from '../../../shared/modules/ng-zorro/ng-zorro.module';
import { BaseComponent } from '../../../shared/utils/base.component';
import { DialogConfig, DialogService } from '../../../shared/services/dialog.service';
import { LeadDetailsService } from '../../../services/lead-details.service';
import { AuthorizationDetailsComponent } from '../../payroll/authorizations/authorization-details/authorization-details.component';
import { UserService } from '../../../services/user.service';
import { ConsumerAuthList, SpendingPlanFilters } from '../../auth-usage-charts/models/authorization.model';
import { AuthorizationService } from '../../auth-usage-charts/services/authorization.service';
import { SpendingPlanComponent } from "../../auth-usage-charts/spending-plan/spending-plan.component";
import { MonthlySpendingPlanSummaryComponent } from "../../auth-usage-charts/spending-plan/monthly-spending-plan-summary/monthly-spending-plan-summary.component";
@Component({
  selector: 'ppl-authorization',
  standalone: true,
  imports: [AccordionComponent, AuthorizeDirective, ListTableComponent, NgZorroModule, AuthorizationDetailsComponent, SpendingPlanComponent, MonthlySpendingPlanSummaryComponent],
  templateUrl: './authorization.component.html',
  styleUrl: './authorization.component.css'
})
export class AuthorizationComponent extends BaseComponent implements OnInit {
  @Input() readOnly = false;
  isSectionEditable = false;
  @ViewChild('dialogContent') dialogContent!: TemplateRef<any>;
  entry: string | null = '';
  title = '';
  tabId = TABS.Authorization;
  programName!: string;
  columnsToBeHidden: any;
  sharedDataKey: any;
  actionsToBeHidden: any;
  lazyLoad = true;
  isPageRestrictionLoaded = false;
  // External users get no row actions on the approved authorizations grid.
  actionButtonsApproved: TableRowActionsModel = new TableRowActions({ view: true });
  actionButtonsErrored: TableRowActionsModel = new TableRowActions();
  approvedData: any;
  erroredOutData: any;
  selectedLead = 0;
  approvedCols: any;
  erroredCols: any;
  @ViewChild('authDialog') authDialog!: TemplateRef<any>;
  private dialogSvc = inject(DialogService);
  id: any;
  private restoreContext: (() => void) | null = null;
  // Passed to ppl-authorization-details as an input.
  selectedAuthorizationId: any = null;
  pageSource: string | null = '';
  pageId = MEMBER_VIEW_PAGE_IDS[this.userService.getFirstSegment()!];
  authorizations: ConsumerAuthList[] = [];
  spendingPlanFilters: SpendingPlanFilters | null = null;
  hasAllocation = false;
  spentCurrentMonth = 0;

  constructor(
    private router: Router,
    private sectionsService: SectionsService,
    private leadDetailsService: LeadDetailsService,
    private userService: UserService,
    private authService: AuthorizationService,
    private authUtility: AuthorizationUtility
  ) {
    super()
  }

  ngOnInit(): void {
    this.id = this.userService.getSelectedLead();
    this.isSectionEditable = this.readOnly ? false : this.authUtility.isSectionEditable(this.pageId, this.tabId, 'Authorization');
    if (this.entry === 'participant') {
      this.title = 'Authorization Summary'
    } else {
      this.title = 'Paystub history'
    }

    this.pageSource = this.userService.getLeadEntry() ?? '';
    this.entry = this.userService.getLeadEntry() ?? '';
    const program = this.userService.getSelectedProgram();
    this.programName = program?.program_code ?? '';
    this.getSectionData();
    this.getGridHeaders();
    this.selectedLead = JSON.parse(this.userService.getSelectedLead()!);
    this.getParticipantAuthorizations(this.programName, this.selectedLead);

    this.getAuthList();
  }

  getAuthList() {
    this.authService.getConsumerAuthList(this.programName, this.selectedLead).subscribe({
      next: (response: any) => {
        const data = response?.responsedata ?? [];
        //remove duplicates based on clientAuth
        this.authorizations = data.filter((auth: ConsumerAuthList, index: number, self: ConsumerAuthList[]) =>
          index === self.findIndex((a) => (
            a.clientAuth === auth.clientAuth
          ))
        );
      },
      error: (err) => {
      }
    });
  }

  async getSectionData() {
    try {
      if (this.entry) {
        this.secData = await this.sectionsService.getJsonConfigurationData(
          STATIC_JSON_DATA_PATH.AUTHORIZATIONS_SECTION_DATA,
          'authorizations'
        );
      }
    } catch (e) {
      console.log('error fetching section data: ', e);
    }
  }

  async getGridHeaders() {
    try {
      if (this.entry) {
        const tempCols = await this.sectionsService.getJsonConfigurationData(STATIC_JSON_DATA_PATH.AUTHORIZATIONS_PARTICIPANT_DETAILS_LIST_HEADERS, this.entry);
        this.approvedCols = tempCols.approvedAuthorizations.columns;
        this.erroredCols = tempCols.erroredAuthorizations.columns;
      }
    } catch (e) {
      console.log("header fetch error: ", e);
    }
  }

  onAddAuthorizations() {
    console.log('process code add button clicked');
    this.router.navigate(['/home/addAuthorizations']);
  }

  action(event: any) {
    console.log('acto', event);
    if (event && event.action === 'view') {
      this.openAuthDialog(event.data);
    }
  }

  openAuthDialog(data: any) {
    this.restoreContext = switchLeadContext(this.sectionsService, this.userService, { flow: 'authorizations', selectedLead: data.authorizationId });
    this.selectedAuthorizationId = data.authorizationId;
    const dialogConfig: DialogConfig = {
      title: `Auth Details for ${data.clientAuth}`,
      okText: 'Deny',
      size: 'xx-large',
      position: 'top',
      showClose: true,
      showButtons: false,
    };

    this.dialogSvc.openDialog(
      this.authDialog,
      dialogConfig,
      () => this.handleCancel(),
      () => this.handleCancel()
    );
  }

  onFiltersChange(filters: SpendingPlanFilters) {
    this.spendingPlanFilters = filters;
  }

  onHasAllocationChange(hasAllocation: boolean) {
    this.hasAllocation = hasAllocation;
    if (!hasAllocation) {
      // The breakdown is unmounted, so its last reading no longer applies.
      this.spentCurrentMonth = 0;
    }
  }

  handleCancel() {
    // Put back the host page's own context (participant or provider) instead of assuming participant.
    this.restoreContext?.();
    this.restoreContext = null;
  }

  getParticipantAuthorizations(programCode: string, id: number) {
    const payload = {
      "pageName": "consumer-auth",
      "programCode": programCode,
      "condition": "and",
      "recordsPerPage": 20,
      "startingPageNumber": "1",
      "orderByColumn": ["participantId"],
      "orderBy": ["desc"],
      "searchParam": [{
        "jsonPath": "participantId",
        "value": id,
        "operator": "equal"
      }]
    }

    this.leadDetailsService.getParticipantAuthorizations(payload).subscribe((data: any) => {
      const authData = data.responsedata.map((item: any) => ({
        ...item,
        consumerName: `${item.consumerFirstName} ${item.consumerLastName}`,
        isDeleted: item.isDeleted === 'Y' ? 'Yes' : item.isDeleted === 'N' ? 'No' : item.isDeleted
      }));
      this.approvedData = authData.filter((item: any) => item.statusLkp.toLowerCase() === 'approved');
      this.approvedData.forEach((item: any) => {
        item.remainingUnits = item.units - item.usedUnits;
      });

      this.erroredOutData = authData.filter((item: any) => item.statusLkp.toLowerCase() !== 'approved');
      this.erroredOutData.forEach((item: any) => {
        item.remainingUnits = item.units - item.usedUnits;
      });
      
      if (!this.approvedData.some((item: any) => item.authFrequencyLkp !== 'Daily')) {
        this.approvedCols = this.approvedCols.filter((col: any) => col.jsonpath !== 'formattedAuthHours');
      }
      if (!this.erroredOutData.some((item: any) => item.authFrequencyLkp !== 'Daily')) {
        this.erroredCols = this.erroredCols.filter((col: any) => col.jsonpath !== 'formattedAuthHours');
      }
    })
  }
}
